// src/pages/LoggedInHome.js
import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Link as MuiLink,
  Grid,
  Stack,
  Skeleton,
  Modal,
  alpha,
  Tooltip,
  Paper,
} from "@mui/material";
import ActivityFeed from "../components/activities/ActivityFeed";
import { styled } from "@mui/material/styles";
import fetchDiveStats from "../utils/getsitestats";
import { useAuth } from "../contexts/AuthContext";
import PhotoCarousel from "../components/media/photocarousel";
import ThemeContext from "../theme/ThemeContext";
import DivesiteMap from "../maps/divesitemap";
import DivesiteDetails from "../components/divesites/divesitedetails";
// import SharedDivesSection from "../components/shared-dives/SharedDivesSection.js";
import {
  GlassButton,
  GlassScrollArea,
} from "../components/layouts/GlassTileLayout"; // Update this path

// Single reducer for all page data
const pageReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: true, error: null };
    
    case 'SET_MAP_DATA':
      return {
        ...state,
        divesiteHeaders: action.payload.sites,
        divesiteStubs: action.payload.stubs,
        loading: false,
        error: null
      };
    
    case 'SET_STATS':
      return {
        ...state,
        stats: action.payload
      };
    
    case 'SET_SELECTED_SITE':
      return {
        ...state,
        selectedSiteDetails: action.payload
      };
    
    case 'SET_MODAL':
      return {
        ...state,
        selectedSiteForModal: action.payload.site,
        openModal: action.payload.open
      };
    
    case 'SET_LEGEND':
      return {
        ...state,
        legendOpen: action.payload
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    default:
      return state;
  }
};

const initialPageState = {
  divesiteHeaders: null,
  divesiteStubs: null,
  stats: {
    diveCount: null,
    diverCount: null,
    siteCount: null,
    error: null,
  },
  selectedSiteDetails: null,
  selectedSiteForModal: null,
  openModal: false,
  legendOpen: false,
  loading: true,
  error: null
};


// Simplified glassmorphic page container
const GlassPageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  position: "relative",
}));

// Simplified welcome header - remove complex pseudo-elements
const GlassWelcomeHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  textAlign: "center",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.85
  )} 0%, ${alpha(theme.palette.background.paper, 0.75)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 20,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
}));

const WelcomeTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
}));

// Simplified content section
const GlassContentSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

// Simplified section header
const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: "1.1rem",
  margin: 0,
}));

const ContentArea = styled(Box)({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  width: "100%",
  position: "relative",
});

const GlassRegisterButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 12,
  color: theme.palette.primary.main,
  fontWeight: 600,
  textTransform: "none",
  padding: theme.spacing(1.5, 3),
  marginBottom: theme.spacing(1),
  transition: "all 0.2s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.15
    )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

// Glassmorphic stats display
// Simplified stats container
const GlassStatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: 16,
  marginBottom: theme.spacing(3),
  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
}));

// Glassmorphic modal
const GlassModal = styled(Modal)(({ theme }) => ({
  "& .modal-content": {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 800,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.95
    )} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
    backdropFilter: "blur(25px)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    borderRadius: 16,
    boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
    padding: theme.spacing(4),
    maxHeight: "90vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      background: alpha(theme.palette.background.default, 0.1),
      borderRadius: 3,
    },
    "&::-webkit-scrollbar-thumb": {
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.3
      )}, ${alpha(theme.palette.primary.main, 0.2)})`,
      borderRadius: 3,
    },
  },
}));

// Legend styled components
const LegendToggle = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1001,
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  color: theme.palette.text.primary,
  fontSize: '0.75rem',
  fontWeight: 'bold',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: theme.shadows[2],
  },
}));

const CollapsibleEnhancedLegend = styled(Paper)(({ theme, open }) => ({
  position: 'absolute',
  top: theme.spacing(5.5),
  right: theme.spacing(1),
  zIndex: 1000,
  padding: open ? theme.spacing(2) : 0,
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  backdropFilter: 'blur(6px)',
  minWidth: open ? 220 : 0,
  maxHeight: open ? 300 : 0,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  opacity: open ? 1 : 0,
  transform: open ? 'translateY(0)' : 'translateY(-10px)',
  border: `1px solid ${theme.palette.divider}`,
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LegendIcon = styled('img')({
  width: 28,
  height: 18,
  objectFit: 'contain',
  flexShrink: 0,
});

const LegendContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
});

const CountBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: '12px',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  minWidth: '24px',
  textAlign: 'center',
  marginLeft: 'auto',
}));

const StatsDisplay = ({ stats }) => {
  if (stats.error) {
    return (
      <Typography color="error" variant="body2">
        {stats.error}
      </Typography>
    );
  }

  if (
    stats.diveCount === null ||
    stats.diverCount === null ||
    stats.siteCount === null
  ) {
    return (
      <Stack spacing={1} width="100%" alignItems="center">
        <Skeleton width="60%" height={24} />
        <Skeleton width="60%" height={24} />
        <Skeleton width="60%" height={24} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} width="100%" alignItems="center">
      <Typography
        variant="h6"
        sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
      >
        Community Stats
      </Typography>
      <Stack
        direction="row"
        spacing={4}
        justifyContent="center"
        flexWrap="wrap"
      >
        <Box textAlign="center">
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {stats.diveCount.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Dives
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {stats.diverCount.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Divers
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {stats.siteCount.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dive Sites
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

const AuthSection = () => {
  const { currentUser } = useAuth();

  if (currentUser) {
    return (
      <Typography variant="body2" gutterBottom>
        Welcome back, <strong>{currentUser.displayName || currentUser.email}</strong>! (
        <MuiLink href="/logout" underline="hover" sx={{ color: "primary.main" }} >
          Log Out
        </MuiLink>
        )
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <GlassRegisterButton size="medium" href="/register">
        Register
      </GlassRegisterButton>
      <Typography variant="body2" color="textSecondary" fontSize="0.85rem">
        Already a member?{" "}
        <MuiLink href="/login" underline="hover" sx={{ color: "primary.main" }}>
          Log In
        </MuiLink>
      </Typography>
    </Box>
  );
};

const LoggedInHome = () => {
  const { currentUser, userObjectId } = useAuth();
  const [stats, setStats] = useState({
    diveCount: null,
    diverCount: null,
    siteCount: null,
    error: null,
  });
  const { isMobile } = useContext(ThemeContext);
  const [divesiteHeaders, setDivesiteHeaders] = useState(null);
  const [selectedSiteDetails, setSelectedSiteDetails] = useState(null);
  const [selectedSiteForModal, setSelectedSiteForModal] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [divesiteStubs, setDivesiteStubs] = useState(null);
  const [loadingMapData, setLoadingMapData] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);

  // Helper function to calculate counts
  const calculateCounts = () => {
    const siteCount = divesiteHeaders?.length || 0;
    const stubCount = divesiteStubs?.filter(stub => stub.stubInfo?.status === 'new').length || 0;
    const totalStubs = divesiteStubs?.length || 0;
    
    return { siteCount, stubCount, totalStubs };
  };

// Update your CountBadge styled component:
const CountBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: '14px',
  padding: '4px 10px',
  fontSize: '1rem !important', // Add !important to override
  fontWeight: 'bold',
  minWidth: '28px',
  textAlign: 'center',
  marginLeft: 'auto',
}));

  useEffect(() => {
    const fetchData = async () => {
      setLoadingMapData(true);
      try {
        let token = null;
        if (currentUser) {
          token = await currentUser.getIdToken();
        }
        const payloadHeader = {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        };

        // Fetch Divesites
        const sitesResponse = await fetch(
          "/api/divesites/",
          payloadHeader
        );
        const sitesJson = await sitesResponse.json();

        if (sitesResponse.ok) {
          setDivesiteHeaders(sitesJson);
        } else {
          console.error("Error fetching divesites:", sitesJson.message);
          setDivesiteHeaders([]);
        }

        // Fetch Divesite Stubs
        const stubsResponse = await fetch(
          "/api/divesitestubs/",
          payloadHeader
        );
        const stubsJson = await stubsResponse.json();
        
        if (stubsResponse.ok) {
          setDivesiteStubs(stubsJson);
        } else {
          console.error("Error fetching divesite stubs:", stubsJson.message);
          setDivesiteStubs([]);
        }
      } catch (e) {
        console.error("Error fetching map data:", e);
        setDivesiteHeaders([]);
        setDivesiteStubs([]);
      } finally {
        setLoadingMapData(false);
      }
    };

    fetchData();
  }, [currentUser]);

  useEffect(() => {
    const getStats = async () => {
      try {
        let token = null;
        if (currentUser) {
          token = await currentUser.getIdToken();
        }
        const fetchedStats = await fetchDiveStats(token);
        setStats(fetchedStats);
      } catch (error) {
        console.error("Error fetching dive stats:", error);
        setStats((prev) => ({ ...prev, error: "Failed to load statistics" }));
      }
    };

    getStats();
  }, [currentUser]);

  const handleMarkerClick = (siteOrStub) => {
    setSelectedSiteDetails(siteOrStub);
    console.log("Marker clicked:", siteOrStub);
  };

  const handleDivesiteNameClick = (siteOrStub) => {
    setSelectedSiteForModal(siteOrStub);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSiteForModal(null);
  };

  return (
    <GlassPageContainer maxWidth="lg">
      {/* Welcome Header */}
      <GlassWelcomeHeader>
        <WelcomeTitle variant="h4" component="h1" gutterBottom>
          Explore Dive Sites
        </WelcomeTitle>
        <AuthSection />

      </GlassWelcomeHeader>        

      {/* Map Section - Full Width */}
      <GlassContentSection sx={{ height: "500px", mb: 3 }}>
        <SectionHeader>
          <SectionTitle>Dive Sites Map</SectionTitle>
        </SectionHeader>
        <ContentArea>
        
        <Box flexGrow={1} sx={{ position: 'relative' }}>
          {loadingMapData ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Stack alignItems="center" spacing={1}>
                <Skeleton variant="rectangular" width={isMobile ? 200 : 400} height={40} />
                <Typography>Loading map data...</Typography>
              </Stack>
            </Box>
          ) : (
            <>
              <DivesiteMap
                key="divesite-map-with-stubs"
                sites={divesiteHeaders || []}
                stubs={divesiteStubs || []}
                onMarkerClick={handleMarkerClick}
                onNameClick={handleDivesiteNameClick}
              />
              
              {/* Enhanced Legend Toggle Button */}
              <LegendToggle
                variant="outlined"
                size="small"
                onClick={() => setLegendOpen(!legendOpen)}
                startIcon={legendOpen ? '▼' : '▶'}
              >
                Map Legend
              </LegendToggle>
              
              {/* Collapsible Enhanced Legend */}
              <CollapsibleEnhancedLegend elevation={4} open={legendOpen}>
                {legendOpen && (
                  <>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        mb: 1.5, 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: 'black',
                        borderBottom: 1,
                        borderColor: 'divider',
                        pb: 1
                      }}
                    >
                      Map Legend
                    </Typography>
                    
                    <Stack spacing={0.5}>
  <Tooltip 
    title="We want to ensure that all divesites at mydivingsites.com are verified by community members and as accurate and up to date as possible, not scraped from other sites or outdated books."
    arrow
    placement="left"
  >
    <LegendItem sx={{ cursor: 'help' }}>
      <LegendIcon src="/diver-down-flag.png" alt="Dive Site" />
      <LegendContent>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Dive Sites
        </Typography>
        <Typography variant="caption" color="black">
          Established locations on 
        </Typography>
        <Typography variant="caption" color="black">
          mydivingsites.com by community divers.
        </Typography>
      </LegendContent>
      <CountBadge sx={{ bgcolor: 'red' }}>
        {calculateCounts().siteCount}
      </CountBadge>
    </LegendItem>
  </Tooltip>
  
  <Tooltip 
    title="A dive location that needs verification and updating by a community member diver. Claim it, dive it, and tell us about it!"
    arrow
    placement="left"
  >
    <LegendItem sx={{ cursor: 'help' }}>
      <LegendIcon src="/diver-down-flag-stub.png" alt="Dive Site Bounty" />
      <LegendContent>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Dive Site Bounties
        </Typography>
        <Typography variant="caption" color="black">
          Sites needing verification. Claim it!!
        </Typography>
      </LegendContent>
      <CountBadge sx={{ bgcolor: 'blue' }}>
        {calculateCounts().stubCount}
      </CountBadge>
    </LegendItem>
  </Tooltip>
</Stack>
                    
                    {/* Total Summary */}
                    <Box 
                      sx={{ 
                        mt: 1.5, 
                        pt: 1.5, 
                        borderTop: 1, 
                        borderColor: 'divider',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="caption" color="black">
                        Total: {calculateCounts().siteCount + calculateCounts().stubCount} locations
                      </Typography>
                      {calculateCounts().totalStubs > calculateCounts().stubCount && (
                        <Typography variant="caption" color="black" display="block">
                          ({calculateCounts().totalStubs - calculateCounts().stubCount} bounties pending review to be Divesites)
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </CollapsibleEnhancedLegend>
            </>
          )}
        </Box>
        </ContentArea>
      </GlassContentSection>

      {/* Grid for Featured Photos and Activity Feed */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Featured Photos Section */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <GlassContentSection sx={{ minHeight: "575px", height: "575px" }}>
            <SectionHeader>
              <SectionTitle>Featured Dive Photos</SectionTitle>
            </SectionHeader>
            <ContentArea
              sx={{
                alignItems: "center",
                justifyContent: "center",
                padding: 2,
              }}
            >
              <PhotoCarousel
                size={isMobile ? "small" : "medium"}
                numImages={10}
                largeSize="small"
                showArrows={true}
                autoPlay={false}
              />
            </ContentArea>
          </GlassContentSection>
        </Grid>

        {/* Global Activity Feed Section */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <GlassContentSection sx={{ minHeight: "575px", height: "575px" }}>
            <SectionHeader>
              <SectionTitle>Global Activity Feed</SectionTitle>
            </SectionHeader>
            <GlassScrollArea>
              <ActivityFeed
                feedType="global"
                autoRefresh={true}
                refreshInterval={30000}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              />
            </GlassScrollArea>
          </GlassContentSection>
        </Grid>
      </Grid>


      {/* Community Stats - Now moved below photos and activities */}
      <GlassStatsContainer>
        <StatsDisplay stats={stats} />
      </GlassStatsContainer>

      {/* Modal for Dive Site Details */}
      <GlassModal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="divesite-details-modal-title"
        aria-describedby="divesite-details-modal-description"
      >
        <Box className="modal-content">
          {selectedSiteForModal && (
            <DivesiteDetails
              divesite={selectedSiteForModal}
              isStub={!!selectedSiteForModal.stubInfo}
            />
          )}
          <GlassButton onClick={handleCloseModal} sx={{ mt: 2 }}>

            Close
          </GlassButton>
        </Box>
      </GlassModal>
    </GlassPageContainer>
  );
};

export default LoggedInHome;
